import React, { useState, useRef } from 'react';
import { Upload, X, Image as ImageIcon, Loader2 } from 'lucide-react';
import { uploadImage } from '../data/api';

const ImageUploader = ({ label, value, onChange, multiple = false }) => {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const handleFiles = async (files) => {
    if (!files.length) return;
    setUploading(true);
    setError('');
    try {
      if (multiple) {
        const urls = [];
        for (const file of files) {
          const result = await uploadImage(file);
          urls.push(result.url);
        }
        const currentUrls = value ? value.split('\n').filter(Boolean) : [];
        onChange([...currentUrls, ...urls].join('\n'));
      } else {
        const result = await uploadImage(files[0]);
        onChange(result.url);
      }
    } catch {
      setError('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/')));
  };

  const handleInputChange = (e) => {
    handleFiles(Array.from(e.target.files));
    e.target.value = '';
  };

  const removeUrl = (urlToRemove) => {
    if (multiple) {
      const urls = value.split('\n').filter(u => u.trim() && u.trim() !== urlToRemove);
      onChange(urls.join('\n'));
    } else {
      onChange('');
    }
  };

  const urls = multiple
    ? (value || '').split('\n').filter(u => u.trim())
    : value ? [value] : [];

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-gray-700">{label}</label>
      {error && <p className="text-xs text-red-600">{error}</p>}

      {/* Uploaded images preview */}
      {urls.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {urls.map((url) => (
            <div key={url} className="relative group w-20 h-16 rounded-lg overflow-hidden border border-gray-200">
              <img src={url} alt="" className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => removeUrl(url)}
                className="absolute top-0 right-0 bg-red-500 text-white rounded-bl-lg p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        data-testid={`image-upload-${label.toLowerCase().replace(/\s/g, '-')}`}
        className={`border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition-all ${
          dragOver ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
        } ${uploading ? 'opacity-60 pointer-events-none' : ''}`}
      >
        {uploading ? (
          <div className="flex items-center justify-center gap-2 text-blue-600">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="text-sm font-medium">Uploading...</span>
          </div>
        ) : (
          <div className="flex items-center justify-center gap-2 text-gray-500">
            <Upload className="w-5 h-5" />
            <span className="text-sm">
              {multiple ? 'Drop images or click to upload' : 'Drop image or click to upload'}
            </span>
          </div>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple={multiple}
          onChange={handleInputChange}
          className="hidden"
        />
      </div>
    </div>
  );
};

export default ImageUploader;
